import React, { useState, useEffect } from 'react';
import * as d3 from 'd3';
import { ChartHeight, ScaleX, ChartMargin, initializeSvg, AxisX, OPACITY_DELTA, MAX_CHART_RENDER_TIME, showTooltip, tooltipDiv, hideTooltip, addLabelForAxisX, addLabelForAxisY, ChartWidth } from './utility';
import { Data, DefaultDataElement, DataElement } from './types';
import { Slides } from '../components/Slides';

const svgId = "chart-tdp";

const scaleY = d3.scaleLog()
    .domain([3e-1, 15e1])
    .range([ChartHeight, 0]);

const yAxis = d3.axisLeft(scaleY)
    .ticks(8).tickValues([0, 1, 5, 10, 25, 50, 75, 150])
    .tickFormat(d3.format('.2f'))
    .tickSizeOuter(0);


export default function Tdp() {
    const [ready, setReady] = useState(false);
    const [hasReset, setHasReset] = useState(false);
    const [allData, setAllData] = useState([...Data]);
    const [prevData, setPrevData] = useState(DefaultDataElement);

    function reset() {
        setAllData([...Data]);
        setPrevData(DefaultDataElement);
        setHasReset(true);

        d3.selectAll('path[data-class="line-connection"]').remove();
    }

    function moveNext() {
        if (allData.length === 0)
            return;

        const nextData: DataElement = allData.shift() || DefaultDataElement;

        setAllData([...allData]);

        const path = d3.select(`svg[id=${svgId}]`)
            .append('g')
            .append('path')
            .datum([prevData as any, nextData as any])
            .attr('transform', `translate(${ChartMargin.left}, ${ChartMargin.bottom})`)
            .attr('fill', 'none')
            .attr('stroke', 'slateGray')
            .attr("stroke-width", 2)
            .attr("d", d3.line()
                .x(d => ScaleX(parseInt((d as any).YEAR, 10)))
                .y(d => scaleY(parseFloat((d as any).TDP))
            )).attr('data-class', 'line-connection');

        setPrevData(nextData);

        d3.select(`path[data-class=line-slide-connection]`).remove();
        d3.select(`svg[id=${svgId}]`)
            .append('path')
            .datum([nextData as any, {YEAR: 1990, TDP: 90}])
            .attr('transform', `translate(${ChartMargin.left}, ${ChartMargin.bottom})`)
            .attr('fill', 'none')
            .attr('stroke', 'slateGray')
            .attr("stroke-width", 1)
            .style('opacity', 0.5)
            .attr("d", d3.line()
                .x(d => ScaleX(parseInt((d as any).YEAR, 10)))
                .y(d => scaleY(parseFloat((d as any).TDP))
            )).attr('data-class', 'line-slide-connection');
            
            d3.select("text[data-class=line-slide-annotation]").remove();
            d3.select(`svg[id=${svgId}]`)
                .append("text").datum(nextData as any)
                .attr("data-class", "line-slide-annotation")
                .attr(`transform`, (d, i, c) => {
                    const val = `translate(${ChartWidth / 2 - 1.5 * ChartMargin.left}, ${ChartHeight / 6 - 16})`;

                    return val;
            }).text(d => (d as any).CPU);


        const pathLength = path.node()?.getTotalLength().toString() || '0';
        path.attr('stroke-dashoffset', pathLength)
            .attr('stroke-dasharray', pathLength)
            .transition()
            .ease(d3.easeExpIn)
            .duration(100)
            .attr('stroke-dashoffset', 0);
    }

    useEffect(() => {
        if(hasReset)
            moveNext();
        setHasReset(false);
    }, [allData, moveNext]);

    let opacityLevel = 0;
    useEffect(() => {
        initializeSvg(svgId);

        const allDataForCircles = [...Data];
        const circleData: DataElement[] = [];
        const interval = setInterval(() => {
            if(allDataForCircles.length === 0) {
                d3.select(`svg[id=${svgId}]`).attr('opacity', 1);
                moveNext();
                setReady(true);
                clearInterval(interval);
                return;
            }

            circleData.push(allDataForCircles.shift() || {YEAR: '1970', TDP: '0'} as any);
            
            d3.select(`svg[id=${svgId}]`).selectAll('circle').remove();
            d3.select(`svg[id=${svgId}]`)
                .attr('opacity', opacityLevel)
                .selectAll('dot')
                .data(circleData)
                .enter()
                .append('circle')
                .attr('fill', 'grey')
                .attr('transform', `translate(${ChartMargin.left}, ${ChartMargin.bottom})`)
                .attr('r', 5)
                .attr('cx', (d, i) => ScaleX(parseInt(d.YEAR, 10)))
                .attr('cy', (d, i) => scaleY(parseFloat(d.TDP || '')))
                .style('opacity', 0.3).on('mouseover', (d, i, c) => {
                    const circle = c[i];
                    circle.setAttribute('r', '10');

                    circle.setAttribute('stroke-width', '2');
                    circle.setAttribute('stroke', 'slateBlue');

                    showTooltip(tooltipDiv, d);
                }).on('mouseout', (d,i,c) => {
                    const circle = c[i];
                    circle.setAttribute('r', '5');
                    circle.removeAttribute('stroke-width');
                    circle.removeAttribute('stroke');

                    hideTooltip(tooltipDiv);
                });

            d3.select(`svg[id=${svgId}]`)
                .selectAll('circle')
                .transition()
                .duration(500)
                .ease(d3.easeLinear)
                .attr('fill', (d) => {
                    const interpolater = d3.scaleLog()
                        .base(2)
                        .domain([0.1, 150])
                        .range(['goldenrod', 'darkred'] as any)
                        .interpolate(d3.interpolateRgb as any);
                    const color = interpolater(parseFloat((d as any).TDP || '0'));
                    return color;
                })
                .style('opacity', 0.9);
            opacityLevel += OPACITY_DELTA
        }, MAX_CHART_RENDER_TIME / Data.length);


        d3.select(`svg[id=${svgId}]`)
            .append('g')
            .attr('transform', `translate(${ChartMargin.left}, ${ChartMargin.bottom})`)
            .call(yAxis);

        d3.select(`svg[id=${svgId}]`)
            .append('g')
            .attr('transform', `translate(${ChartMargin.left - 4}, ${ChartHeight - 1 - ChartMargin.bottom})`)
            .call(AxisX);

        addLabelForAxisX('Year', svgId);
        addLabelForAxisY('TDP (in Watts)', svgId);
    }, []);

    return (
        <div className="chart-container">
            <div className='container'>
                <div className="row chart-description justify-content-center">
                    <h3>TDP over Time</h3>
                    <p>
                        Included with this data is the thermal design power of each CPU. The TDP will usually present some indication on the power output of the CPU. 
                        Higher levels of TDP in watts indicate more power being used by the CPU.
                    </p>
                </div>
                <div className='row'>
                    <div className='d-flex justify-content-start'>
                        <svg id={svgId}></svg>
                    </div>
                    <div className='d-flex justify-content-end'>
                        <Slides data={prevData} reset={reset} moveNext={moveNext} ready={ready} />
                    </div>
                </div>
                <div className="row">
                    <div className="attribution col-6 offset-md-9">
                        Data Source: <a target="_blank" rel="noopener noreferrer" href="http://www.cpu-world.com/">CPU-World</a>
                    </div>
                </div>
            </div>
        </div>
    );
}